<template>
  <div class="ncrm-mailjet-webhooks">
    <!-- Status banner: status + partial-failure warning + actions -->
    <v-card variant="outlined" class="mb-3">
      <v-card-text>
        <div class="d-flex align-center flex-wrap ga-3">
          <div class="ncrm-mailjet-webhooks__status">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.integrations.mailjet.webhooks.statusLabel") }}
            </div>
            <v-chip :color="statusColor">
              <v-icon start size="14">{{ statusIcon }}</v-icon>
              {{ statusLabel }}
            </v-chip>
          </div>

          <div v-if="webhookUrl" class="ncrm-mailjet-webhooks__url">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.integrations.mailjet.webhooks.urlLabel") }}
            </div>
            <code class="text-caption">{{ webhookUrl }}</code>
          </div>

          <div v-if="account.webhook_activated_at" class="ncrm-mailjet-webhooks__activated">
            <div class="text-caption text-medium-emphasis">
              {{ $t("ncrm.integrations.mailjet.webhooks.activatedAtLabel") }}
            </div>
            <span class="text-caption">{{ formatDate(account.webhook_activated_at) }}</span>
          </div>

          <v-spacer />

          <div class="ncrm-mailjet-webhooks__actions">
            <v-btn
              variant="tonal"
              size="small"
              :loading="refreshing"
              :disabled="!accountId || busy"
              @click="refreshStatus"
            >
              <v-icon start size="16">mdi-refresh</v-icon>
              {{ $t("ncrm.integrations.mailjet.webhooks.refresh") }}
            </v-btn>
            <v-btn
              v-if="isActive"
              variant="tonal"
              color="warning"
              size="small"
              :loading="disabling"
              :disabled="!accountId || busy"
              @click="confirmDisable"
            >
              <v-icon start size="16">mdi-power</v-icon>
              {{ $t("ncrm.integrations.mailjet.webhooks.disable") }}
            </v-btn>
            <v-btn
              v-else
              variant="flat"
              color="primary"
              size="small"
              :loading="activating"
              :disabled="!accountId || busy"
              @click="activate"
            >
              <v-icon start size="16">mdi-power-plug</v-icon>
              {{ $t("ncrm.integrations.mailjet.webhooks.activate") }}
            </v-btn>
            <v-btn
              variant="text"
              size="small"
              :loading="rotating"
              :disabled="!accountId || busy"
              @click="confirmRotate"
            >
              <v-icon start size="16">mdi-key-change</v-icon>
              {{ $t("ncrm.integrations.mailjet.webhooks.rotate") }}
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="account.webhook_last_error"
          type="warning"
          density="compact"
          class="mt-3"
        >
          {{ account.webhook_last_error }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Mailjet-side registered callbacks -->
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center">
        <span class="text-subtitle-2">
          {{ $t("ncrm.integrations.mailjet.webhooks.registeredTitle") }}
        </span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.mailjet.webhooks.registeredCount", { count: mailjetRows.length }) }}
        </span>
      </v-card-title>
      <v-divider />
      <v-alert
        v-if="mailjetError"
        type="error"
        density="compact"
        class="ma-3"
      >
        {{ mailjetError }}
      </v-alert>
      <v-data-table
        :headers="callbackHeaders"
        :items="mailjetRows"
        :loading="refreshing"
        density="comfortable"
        class="elevation-0"
        item-key="ID"
      >
        <template #item.Status="{ item }">
          <v-chip
            :color="item.Status === 'alive' ? 'success' : 'error'"
            size="x-small"
          >
            {{ item.Status }}
          </v-chip>
        </template>
        <template #item.Url="{ item }">
          <code class="text-caption">{{ redact(item.Url) }}</code>
        </template>
        <template #no-data>
          <v-alert type="info" class="ma-2">
            {{ $t("ncrm.integrations.mailjet.webhooks.empty") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <!-- Confirm dialogs -->
    <v-dialog v-model="confirmDialog.show" max-width="480">
      <v-card>
        <v-card-title>{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmDialog.show = false">
            {{ $t("ncrm.integrations.mailjet.dialog.actions.cancel") }}
          </v-btn>
          <v-btn
            :color="confirmDialog.color"
            variant="flat"
            @click="executeConfirmed"
          >
            {{ confirmDialog.confirmLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmMailjetWebhooksTab",
  props: {
    account: { type: Object, required: true },
  },
  emits: ["notify", "account-updated"],
  data() {
    return {
      refreshing: false,
      activating: false,
      disabling: false,
      rotating: false,
      mailjetRows: [],
      mailjetError: null,
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        confirmLabel: "",
        color: "primary",
        action: null,
      },
    };
  },
  computed: {
    accountId() {
      return this.account?.id || null;
    },
    isActive() {
      return this.account?.webhook_status === "active";
    },
    statusColor() {
      const s = this.account?.webhook_status;
      if (s === "active") {
        // Partial-failure note: status='active' but last_error populated
        return this.account?.webhook_last_error ? "warning" : "success";
      }
      if (s === "error") return "error";
      return "default";
    },
    statusIcon() {
      const s = this.account?.webhook_status;
      if (s === "active") return this.account?.webhook_last_error ? "mdi-alert" : "mdi-check-circle";
      if (s === "error") return "mdi-alert-circle";
      return "mdi-power-off";
    },
    statusLabel() {
      const s = this.account?.webhook_status || "none";
      return this.$t(`ncrm.integrations.mailjet.webhooks.status.${s}`);
    },
    webhookUrl() {
      return this.account?.webhook_url || null;
    },
    busy() {
      return this.refreshing || this.activating || this.disabling || this.rotating;
    },
    callbackHeaders() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.webhooks.headers.eventType"), key: "EventType" },
        { title: this.$t("ncrm.integrations.mailjet.webhooks.headers.status"), key: "Status" },
        { title: this.$t("ncrm.integrations.mailjet.webhooks.headers.url"), key: "Url", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.webhooks.headers.mailjetId"), key: "ID", align: "end" },
      ];
    },
  },
  watch: {
    accountId: {
      handler(id) {
        if (id) this.refreshStatus();
        else this.mailjetRows = [];
      },
      immediate: true,
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "—";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    // Replace the secret part of a webhook URL with <redacted> so we don't
    // accidentally print real secrets in screenshots or logs.
    redact(url) {
      if (!url) return "";
      return String(url).replace(
        /(\/integrations\/mailjet\/webhook\/[^/]+\/)[^/?#]+/,
        "$1<redacted>"
      );
    },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    notify(color, message) {
      this.$emit("notify", { color, message });
    },

    async refreshStatus() {
      if (!this.accountId) return;
      this.refreshing = true;
      this.mailjetError = null;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/webhooks/status`
        );
        this.mailjetRows = Array.isArray(data?.mailjet_rows) ? data.mailjet_rows : [];
        this.mailjetError = data?.mailjet_error || null;
        // Parent updates account state through the status response itself.
        if (data?.local) {
          this.$emit("account-updated", { ...this.account, ...data.local });
        }
      } catch (error) {
        this.notify(
          "error",
          this.extractError(error, "ncrm.integrations.mailjet.webhooks.errors.status")
        );
      } finally {
        this.refreshing = false;
      }
    },

    async activate() {
      if (!this.accountId) return;
      this.activating = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/webhooks/setup`,
          {}
        );
        this.notify(
          "success",
          this.$t("ncrm.integrations.mailjet.webhooks.activated", {
            created: data?.created?.length || 0,
            skipped: data?.skipped?.length || 0,
          })
        );
        await this.refreshStatus();
      } catch (error) {
        this.notify(
          "error",
          this.extractError(error, "ncrm.integrations.mailjet.webhooks.errors.activate")
        );
      } finally {
        this.activating = false;
      }
    },

    confirmDisable() {
      this.confirmDialog = {
        show: true,
        title: this.$t("ncrm.integrations.mailjet.webhooks.confirmDisable.title"),
        message: this.$t("ncrm.integrations.mailjet.webhooks.confirmDisable.message"),
        confirmLabel: this.$t("ncrm.integrations.mailjet.webhooks.disable"),
        color: "warning",
        action: this.disable,
      };
    },
    confirmRotate() {
      this.confirmDialog = {
        show: true,
        title: this.$t("ncrm.integrations.mailjet.webhooks.confirmRotate.title"),
        message: this.$t("ncrm.integrations.mailjet.webhooks.confirmRotate.message"),
        confirmLabel: this.$t("ncrm.integrations.mailjet.webhooks.rotate"),
        color: "warning",
        action: this.rotateSecret,
      };
    },
    async executeConfirmed() {
      const action = this.confirmDialog.action;
      this.confirmDialog.show = false;
      if (typeof action === "function") await action();
    },

    async disable() {
      if (!this.accountId) return;
      this.disabling = true;
      try {
        await axios.delete(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/webhooks`
        );
        this.notify(
          "success",
          this.$t("ncrm.integrations.mailjet.webhooks.disabled")
        );
        await this.refreshStatus();
      } catch (error) {
        this.notify(
          "error",
          this.extractError(error, "ncrm.integrations.mailjet.webhooks.errors.disable")
        );
      } finally {
        this.disabling = false;
      }
    },

    async rotateSecret() {
      if (!this.accountId) return;
      this.rotating = true;
      try {
        // Backend returns the plaintext secret ONCE; the user must then
        // re-run activate to push the new URL to Mailjet. We auto-chain.
        await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/webhooks/rotate-secret`,
          {}
        );
        this.notify(
          "info",
          this.$t("ncrm.integrations.mailjet.webhooks.rotated")
        );
        // Immediately re-activate so the new secret hits Mailjet.
        await this.activate();
      } catch (error) {
        this.notify(
          "error",
          this.extractError(error, "ncrm.integrations.mailjet.webhooks.errors.rotate")
        );
      } finally {
        this.rotating = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-webhooks__status,
.ncrm-mailjet-webhooks__url,
.ncrm-mailjet-webhooks__activated {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ncrm-mailjet-webhooks__url code {
  max-width: 480px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  padding: 2px 6px;
  background: var(--app-surface-2, rgba(0, 0, 0, 0.04));
  border-radius: 4px;
}
.ncrm-mailjet-webhooks__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
</style>
